import { isApiError, unwrap, type ApiError, type SagotApi } from "@sagot/api-client";
import { SERVICE_ERROR_CODE_VALUES, type ServiceErrorCode } from "@sagot/constants";
import { useMutation, useQuery, useQueryClient, type QueryKey, type UseMutationResult, type UseQueryResult } from "@tanstack/react-query";
import { useApi } from "../../session/session-provider";
import { useWorkspaceId } from "../../session/workspace-context";

/** What an `openapi-fetch` call resolves to: the typed body or the error body, and the raw response. */
export interface ServiceResult {
  data?: unknown;
  error?: unknown;
  response: Response;
}

/** The typed 2xx body of a call (`unwrap` throws a normalized `ApiError` otherwise); the same shape `unwrap` returns. */
export type ServiceData<R extends ServiceResult> = R extends { data: infer D } ? D : R extends { data?: infer D } ? Exclude<D, undefined> : never;

export interface ServiceQueryOptions<R extends ServiceResult> {
  queryKey: QueryKey;
  /** One call on the generated client, e.g. `(api) => api.GET("/v1/tickets", { params: { query } })`: path, params and body are checked against the OpenAPI types. */
  request: (api: SagotApi, signal: AbortSignal) => Promise<R>;
  enabled?: boolean;
  staleTime?: number;
}

/** `useQuery` over the generated client: the body is typed from the path, failures arrive as `ApiError`. */
export function useServiceQuery<R extends ServiceResult>(options: ServiceQueryOptions<R>): UseQueryResult<ServiceData<R>, ApiError> {
  const api = useApi();
  const { queryKey, request, enabled, staleTime } = options;
  return useQuery<ServiceData<R>, ApiError>({
    queryKey,
    queryFn: ({ signal }) => unwrap(request(api, signal)),
    enabled,
    staleTime,
  });
}

export interface ServiceMutationOptions<V, R extends ServiceResult> {
  request: (api: SagotApi, variables: V) => Promise<R>;
  /** Keys to refresh after success, from the workspace id: `(ws) => [serviceKeys.tickets(ws)]`. */
  invalidate?: (ws: string, variables: V, data: ServiceData<R>) => readonly QueryKey[];
}

/** `useMutation` over the generated client; refreshes the listed keys once the server has accepted the change. */
export function useServiceMutation<V, R extends ServiceResult>(options: ServiceMutationOptions<V, R>): UseMutationResult<ServiceData<R>, ApiError, V> {
  const api = useApi();
  const ws = useWorkspaceId();
  const queryClient = useQueryClient();
  const { request, invalidate } = options;
  return useMutation<ServiceData<R>, ApiError, V>({
    mutationFn: (variables) => unwrap(request(api, variables)),
    onSuccess: async (data, variables) => {
      const keys = invalidate?.(ws, variables, data) ?? [];
      await Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
    },
  });
}

/** The Service error code of a failed call (`invalid_transition`, `ticket_changed`…), or null for any other failure. */
export function serviceErrorCode(error: unknown): ServiceErrorCode | null {
  if (!isApiError(error)) return null;
  return SERVICE_ERROR_CODE_VALUES.find((code) => code === error.code) ?? null;
}
